import { useState } from "react"
import { Badge, Box, Group, ScrollAreaAutosize, Title, Tooltip, UnstyledButton } from "@mantine/core"
import { Link, useLocation } from "react-router-dom"
import { useNavigate } from "react-router-dom"
import { ILink } from "../../utils/menu"
import classes from "./DoubleNavbar.module.scss"
import { useHeaderState } from "@/shared/state/header.state"

interface DoubleNavbarProps {
  links: ILink[]
  onLinkClick?: () => void
  opened?: boolean
  onOpen?: () => void
}

const findActiveSection = (links: ILink[], path: string) =>
  links.find((link) => {
    if (link.to && (path === link.to || (link.to !== "/" && path.startsWith(`${link.to}/`)))) {
      return true
    }
    return link.children?.some((child) => (child.to && (path === child.to || path.startsWith(`${child.to}/`))) || child.children?.some((grandChild) => grandChild.to && (path === grandChild.to || path.startsWith(`${grandChild.to}/`))))
  })

const getFirstVisibleTo = (link: ILink): string | undefined => {
  if (link.to) return link.to
  if (link.children) {
    for (const child of link.children) {
      if (child.visible) {
        const firstTo = getFirstVisibleTo(child as ILink)
        if (firstTo) return firstTo
      }
    }
  }
  return undefined
}

/** Bandera de un país por su código (imagen: los emojis de bandera no se ven en Windows). */
const Flag = ({ code }: { code: string }) => <img src={`https://flagcdn.com/w40/${code}.png`} alt={code.toUpperCase()} width={20} height={14} style={{ borderRadius: 2, flexShrink: 0, boxShadow: "0 0 0 1px rgba(0,0,0,.15)" }} />

export function DoubleNavbar({ links, onLinkClick, opened = true, onOpen }: DoubleNavbarProps) {
  const location = useLocation()
  const { navigationContext } = useHeaderState()
  const activePath = navigationContext?.pathname === location.pathname ? navigationContext.activePath : location.pathname
  const navigate = useNavigate()

  const [activeSection, setActiveSection] = useState<string>(() => {
    const active = findActiveSection(links, activePath)
    return active?.label || (links.length > 0 ? links[0].label : "")
  })

  const [prevPath, setPrevPath] = useState(activePath)
  const [prevLinks, setPrevLinks] = useState(links)

  if (activePath !== prevPath || links !== prevLinks) {
    setPrevPath(activePath)
    setPrevLinks(links)

    const active = findActiveSection(links, activePath)
    if (active) {
      setActiveSection(active.label)
    } else if (links.length > 0 && !activeSection) {
      setActiveSection(links[0].label)
    }
  }

  const handleMainLinkClick = (link: ILink) => {
    setActiveSection(link.label)

    const firstTo = getFirstVisibleTo(link)

    if (firstTo) {
      navigate(firstTo)
      if (onLinkClick) onLinkClick()
    } else {
      if (!opened && onOpen) {
        onOpen()
      }
    }
  }

  const mainLinks = links
    .filter((link) => link.visible)
    .map((link) => (
      <Tooltip label={link.label} position="right" withArrow transitionProps={{ duration: 0 }} key={link.label}>
        <UnstyledButton onClick={() => handleMainLinkClick(link)} className={classes.mainLink} data-active={link.label === activeSection || undefined} aria-label={link.label}>
          <link.icon size={21} stroke={1.5} />
        </UnstyledButton>
      </Tooltip>
    ))

  const activeLinkData = links.find((l) => l.label === activeSection)
  const secondaryLinks = activeLinkData?.children
    ?.filter((link) => link.visible)
    .map((link) => {
      if (link.to) {
        return (
          <Link className={classes.link} data-active={activePath === link.to || (link.to !== "/" && activePath.startsWith(`${link.to}/`)) || undefined} to={link.to} key={link.label} onClick={onLinkClick}>
            {link.label}
          </Link>
        )
      }

      // Handle second level children if any (like in Inventario -> Activos)
      return (
        <Box key={link.label}>
          <Box px="md" py="xs">
            <Group gap={8} wrap="nowrap">
              {link.flag && <Flag code={link.flag} />}
              <Title order={6} size="13px" c="dimmed" tt="uppercase">
                {link.label}
              </Title>
            </Group>
          </Box>
          {link.children
            ?.filter((c) => c.visible)
            .map((child) => (
              <Link className={classes.link} data-active={(child.to && (activePath === child.to || activePath.startsWith(`${child.to}/`))) || undefined} to={child.to || "#"} key={child.label} onClick={onLinkClick}>
                {child.badge || link.flag ? <Group justify="space-between" wrap="nowrap"><Group gap={8} wrap="nowrap">{link.flag && <Flag code={link.flag} />}<span>{child.label}</span></Group>{child.badge ? <Badge size="sm" color="orange" circle>{child.badge}</Badge> : null}</Group> : child.label}
              </Link>
            ))}
        </Box>
      )
    })

  return (
    <nav className={classes.navbar}>
      <div className={classes.wrapper}>
        <div className={classes.aside}>
          <ScrollAreaAutosize mih="100%" mah={370} mx="auto" scrollbarSize={0} scrollHideDelay={500}>
            <div className={classes.mainLinksInner}>{mainLinks}</div>
          </ScrollAreaAutosize>
        </div>
        {opened && (
          <div className={classes.main}>
            <Title order={1} className={classes.title}>
              {activeSection}
            </Title>

            {secondaryLinks}
            {activeLinkData?.to && !activeLinkData.children && (
              <Link className={classes.link} data-active={activePath === activeLinkData.to || undefined} to={activeLinkData.to} onClick={onLinkClick}>
                Ir a {activeLinkData.label}
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  )
}
